import { useCallback, useEffect, useState } from 'react';

const cleanIds = (value) => Array.isArray(value)
  ? [...new Set(value.filter(id => typeof id === 'string' && id.length > 0 && id.length <= 512))].slice(0, 128)
  : [];
const readPins = (key) => {
  try { return key ? cleanIds(JSON.parse(localStorage.getItem(key) || '[]')) : []; }
  catch { return []; }
};

// Entries that already have their own action-bar tile (Druidic Warrior cantrips) are not pinned a second time.
export const canPinAttack = (item) => Boolean(item?.id && !item.resolvableLegacy && !item.hasActionBarTile
  && (['weapon', 'unarmed'].includes(item.kind) || item.canAttackRoll || item.authoritative
    || item.buffDefinition || item.healingDefinition || item.manualCast));

export const LIGHT_EXTRA_SUFFIX = ':light-extra';
/**
 * Equipped weapons, Unarmed Strike and taken spells get a tile without being pinned. `takenSpell` leaves out spells with a
 * tile of their own or that a tile can't cast; a weapon's Light extra attack follows the weapon, and smite spells cast
 * from their prompt.
 */
export const isAutoTile = (item) => canPinAttack(item) && (['weapon', 'unarmed'].includes(item.kind)
  ? !item.id.endsWith(LIGHT_EXTRA_SUFFIX) : Boolean(item.takenSpell && !item.smitePrompt));
/** The tiles to show: what's pinned, then everything automatic the player hasn't taken off the bar. */
export const shownAttackIds = (items, pinnedIds, hiddenIds = []) => [...new Set([...pinnedIds, ...items.filter(isAutoTile).map((item) => item.id)])]
  .filter((id) => !hiddenIds.includes(id));

export function resolveQuickAttacks(items, pinnedIds) {
  return pinnedIds.flatMap(id => {
    const matches = items.filter(item => item.id === id && canPinAttack(item));
    return matches.length === 1 ? matches : [];
  });
}

export default function useQuickAttacks(characterId) {
  const key = characterId ? `quick-attacks:${characterId}` : null;
  const [state, setState] = useState(() => ({ key, ids: readPins(key) }));
  useEffect(() => { setState({ key, ids: readPins(key) }); }, [key]);
  const togglePin = useCallback((id) => {
    if (!key || typeof id !== 'string' || !id || id.length > 512) return;
    setState(previous => {
      const ids = previous.key === key ? previous.ids : readPins(key);
      const next = ids.includes(id) ? ids.filter(value => value !== id) : cleanIds([...ids, id]);
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* Keep working for this session. */ }
      return { key, ids: next };
    });
  }, [key]);
  return { pinnedIds: state.key === key ? state.ids : [], togglePin };
}
